/**
 * x-video directive.
 *
 * Manages a `<video>` element within an Alpine component. Handles lazy source
 * loading, intersection-based autoplay/pause, play/pause/volume controls, and
 * integration with Swiper carousels via the `$swiperVideo` magic helper.
 *
 * Videos that are part of a Swiper slider should use `$swiperVideo` so their
 * playback is coordinated with slide changes rather than by intersection.
 *
 * @modifiers
 *   exclusive - When present, pauses all other registered video elements before
 *               playing this one.
 *
 * @events dispatched
 *   video-playing - Fired when the video begins playing.
 *   video-pause   - Fired when the video is paused.
 *   video-ended   - Fired when a non-looping video reaches its end.
 *
 * @magic
 *   $swiperVideo - Returns a function that, when called with a Swiper instance,
 *                  registers the carousel and manages video playback on slide
 *                  change, autoplay, and intersection events.
 *
 * @external
 *   el._video - Imperative handle attached to the `x-video` root element for
 *               controlling playback from outside its Alpine scope, e.g.
 *               `document.querySelector('[x-video]')._video.toggle()`.
 *               Exposes `toggle()`, a read-only `isPlaying` getter, and
 *               `setManualPause(paused)` for syncing manual-pause intent across
 *               multiple independent instances that share one external control
 *               (e.g. responsive breakpoint variants of the same video).
 *
 * @example
 *   <div x-video>
 *     <video autoplay muted loop playsinline>
 *       <source data-video-source="/video.mp4" type="video/mp4">
 *     </video>
 *     <button @click="togglePlayPause">Play/Pause</button>
 *   </div>
 *
 *   // Swiper integration:
 *   <div x-data="{ init() { this.$swiperVideo(swiperInstance); } }">…</div>
 */
(() => {
  /**
   * Maps directive root elements to their `<video>` elements. Used by the
   * `exclusive` modifier to pause all other videos before playing.
   * @type {Map<Element, HTMLVideoElement>}
   */
  const VideoElements = new Map();

  /**
   * Maps directive root elements to their `<video>` elements when those videos
   * are controlled by an external Swiper instance. Intersection-based autoplay
   * is skipped for externally-controlled videos.
   * @type {Map<Element, HTMLVideoElement>}
   */
  const ExternallyControlledVideos = new Map();

  /**
   * Returns true when the element is currently visible in the document,
   * accounting for CSS `display: none`, `visibility: hidden`, and media queries.
   * Uses the `checkVisibility()` API available in modern browsers.
   * @param {Element|null} el - The element to check.
   * @returns {boolean}
   */
  function isElementVisible(el) {
    if (!el) return false;

    // Modern browsers: Chrome 105+, Firefox 106+, Safari 17.4+.
    return el?.checkVisibility?.() ?? false;
  }

  const root = (Alpine, el, { exclusive = false }) => {
    const video = el.querySelector("video");
    const autoplay = video.autoplay;
    const muted = video.muted;

    /**
     * Returns true when the video is actively playing.
     * @returns {boolean}
     */
    function isPlaying() {
      return !!(
        video.currentTime > 0 &&
        !video.paused &&
        !video.ended &&
        video.readyState > 2
      );
    }

    VideoElements.set(el, video);

    Alpine.bind(video, () => ({
      ["x-ref"]: "video",

      /**
       * Marks the component as playing when the video fires its `playing` event
       * and dispatches `video-playing`. Bound to the native event rather than the
       * `play()` method so it fires regardless of what triggered playback —
       * an explicit call, native `autoplay`, or browser-native controls.
       */
      ["@playing"]() {
        if (this.$data.manuallyPaused) {
          video.pause();
          return;
        }

        this.$data.playing = true;
        this.$dispatch("video-playing");
      },

      /**
       * Marks the component as not playing when the video fires its `pause` event
       * and dispatches `video-pause`. See `@playing` above for why this is bound
       * to the native event rather than the `pause()` method.
       */
      ["@pause"]() {
        this.$data.playing = false;
        this.$dispatch("video-pause");
      },

      /** Dispatches `video-ended` when a non-looping video reaches its end. */
      ["@ended"]() {
        if (!this.$el.loop) {
          this.$dispatch("video-ended");
        }
      },

      /** Syncs the `muted` reactive property when the video's volume changes. */
      ["@volumechange"]() {
        this.$data.muted = this.$el.muted;
      },

      /**
       * Lazy-loads video sources and autoplays when the video enters the viewport.
       * Skipped when the video is externally controlled by a Swiper instance.
       */
      ["x-intersect:enter"]() {
        if (ExternallyControlledVideos.has(el)) {
          return; // Skip — externally controlled.
        }

        // A never-loaded video keeps the native `autoplay` attribute, which
        // starts playback on its own as soon as `loadVideoSources` below
        // triggers `video.load()` — bypassing manual-pause state entirely,
        // since that isn't a JS `play()` call. Strip it first so a video
        // that becomes visible for the first time while manually paused
        // (e.g. a different breakpoint's video after a resize) never
        // visibly starts playing at all.
        if (this.$data.manuallyPaused) {
          video.removeAttribute("autoplay");
        }

        loadVideoSources(video);
        if (
          autoplay &&
          !this.$data.isPlaying &&
          !this.$data.manuallyPaused &&
          isElementVisible(video)
        ) {
          this.$data.play();
        }
      },

      /**
       * Pauses the video when it leaves the viewport.
       * Skipped when the video is externally controlled by a Swiper instance.
       */
      ["x-intersect:leave"]() {
        if (ExternallyControlledVideos.has(el)) {
          return; // Skip — externally controlled.
        }

        if (this.$data.isPlaying) this.$data.pause();
      },
    }));

    Alpine.bind(el, () => ({
      ["x-data"]() {
        return {
          muted: muted,

          /**
           * Exposes an imperative play/pause handle on the root element so
           * external controls can operate this video without reaching into
           * this component's Alpine scope.
           */
          init() {
            const data = this;

            el._video = {
              toggle: () => data.togglePlayPause(),
              get isPlaying() {
                return data.isPlaying;
              },
              setManualPause(paused) {
                data.manuallyPaused = paused;
              },
            };
          },

          /**
           * Removes this element from the global registries on teardown.
           */
          destroy() {
            VideoElements.delete(el);
            ExternallyControlledVideos.delete(el);
            delete el._video;
          },

          /**
           * Plays the video. When `exclusive` is true, pauses all other
           * registered video elements first. Updates `playing` and dispatches
           * `video-playing` synchronously on success, rather than waiting on
           * the native `playing` event — which can be delayed or coalesced
           * away entirely when a `pause()` follows a `play()` in quick
           * succession (e.g. rapid scroll-triggered intersect enter/leave).
           * The native `@playing` listener (bound above) still covers
           * playback started outside this method (e.g. native `autoplay`).
           * @returns {Promise<void>}
           */
          async play() {
            if (exclusive) {
              VideoElements.forEach((node) => {
                node.pause();
              });
            }

            try {
              await video.play();
              this.playing = true;
              this.$dispatch("video-playing");
            } catch {
              // Playback failed or was interrupted (e.g. a `pause()` call
              // raced this one) — leave state to whichever native event, if
              // any, actually reflects what happened.
            }
          },

          /**
           * Pauses the video. Updates `playing` and dispatches `video-pause`
           * synchronously — see `play()` above for why this doesn't wait on
           * the native `pause` event. The native `@pause` listener (bound
           * above) still covers playback stopped outside this method.
           */
          pause() {
            video.pause();
            this.playing = false;
            this.$dispatch("video-pause");
          },

          /**
           * Toggles between play and pause based on the current playing state.
           * Tracks manual intent via `manuallyPaused` so intersection-based
           * auto-resume doesn't override a user-initiated pause.
           */
          togglePlayPause() {
            if (this.isPlaying) {
              this.manuallyPaused = true;
              this.pause();
            } else {
              this.manuallyPaused = false;
              this.play();
            }
          },

          /**
           * Toggles the video's muted state.
           */
          toggleVolume() {
            this.$refs.video.muted = !this.muted;
          },

          /**
           * Returns true when the video element has an audio track.
           * @returns {boolean}
           */
          get hasAudio() {
            return (
              video.mozHasAudio ||
              Boolean(video.webkitAudioDecodedByteCount) ||
              Boolean(video.audioTracks && video.audioTracks.length)
            );
          },

          /**
           * Returns true when the video is currently playing.
           * @returns {boolean}
           */
          get isPlaying() {
            return this.playing || isPlaying();
          },
        };
      },
    }));
  };

  /**
   * Lazy-loads video `<source>` elements by promoting `data-video-source` or
   * `data-src` attributes to `src`. Calls `video.load()` when the video has not
   * yet been loaded.
   * @param {HTMLVideoElement} video - The video element whose sources to load.
   */
  function loadVideoSources(video) {
    Array.from(video.children).forEach((source) => {
      const lazySrc = source.dataset.videoSource || source.dataset.src;
      if (lazySrc && !source.getAttribute("src")) {
        source.setAttribute("src", lazySrc);
      }
    });
    if (video.readyState === 0) video.load();
  }

  /**
   * Schedules a callback during browser idle time, falling back to `setTimeout`
   * when `requestIdleCallback` is unavailable.
   * @type {(cb: Function) => void}
   */
  const scheduleIdle =
    typeof requestIdleCallback !== "undefined"
      ? (cb) => requestIdleCallback(cb)
      : (cb) => setTimeout(cb, 0);

  /**
   * Registers a Swiper instance for coordinated video control. Marks all
   * videos in the slider as externally controlled, preloads their sources
   * during idle time, and hooks into Swiper events to play/pause videos on
   * slide change, autoplay, and intersection.
   * @param {object} Alpine - The Alpine.js instance.
   * @param {object} swiper - The Swiper instance to control.
   * @param {Element} el - The Alpine root element that wraps the slider.
   */
  function handleSwiperVideoControl(Alpine, swiper, el) {
    swiper.slides.forEach((slide) => {
      const videoContainers = slide.querySelectorAll("[x-video]");

      videoContainers.forEach((container) => {
        const video = container.querySelector("video");

        if (video) {
          ExternallyControlledVideos.set(container, video);
          // Preload sources during idle time to avoid blocking the main thread.
          scheduleIdle(() => loadVideoSources(video));
        }
      });
    });

    /**
     * Pauses all videos across all slides.
     */
    const pauseAllVideos = () => {
      swiper.slides.forEach((slide) => {
        const videoContainers = slide.querySelectorAll("[x-video]");

        videoContainers.forEach((container) => {
          const video = container.querySelector("video");

          if (video && VideoElements.has(container)) {
            video.pause();
          }
        });
      });
    };

    /**
     * Plays all autoplay videos in the currently active slide.
     * @param {boolean} [resume=false] - When true, resumes from the current
     *   time rather than rewinding to 0.
     */
    const playActiveVideo = async (resume = false) => {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (!activeSlide) return;

      activeSlide.querySelectorAll("[x-video]").forEach((container) => {
        const video = container.querySelector("video");

        if (video && video.autoplay) {
          loadVideoSources(video);

          Alpine.nextTick(() => {
            if (!resume) video.currentTime = 0;

            video.play().catch((error) => {
              // Fixes issue when trying to play, and a new load process has started.
              if (video.readyState === 0) {
                video.addEventListener(
                  "canplay",
                  () => {
                    playActiveVideo();
                  },
                  { once: true },
                );
              }
            });
          });
        }
      });
    };

    const handleSlideChange = () => {
      pauseAllVideos();

      if (Alpine.$data(el).autoplayPaused) return;

      playActiveVideo();
    };

    swiper.on("slideChange", handleSlideChange);
    swiper.on("resize", handleSlideChange);
    swiper.on("autoplayPause", () => pauseAllVideos());
    swiper.on("autoplayResume", () => {
      if (Alpine.$data(el).autoplayPaused) {
        swiper.autoplay.pause();
        return;
      }

      playActiveVideo(true);
    });

    // Handle the initial slide on registration.
    handleSlideChange();

    // Bind the element to pause/resume on intersection.
    Alpine.bind(el, () => ({
      ["x-intersect:leave"]() {
        if (swiper.params.autoplay.enabled && !swiper.autoplay.paused) {
          swiper.autoplay.pause();
        }

        this.$nextTick(() => {
          pauseAllVideos();
        });
      },
      ["x-intersect:enter"]() {
        if (this.$data.autoplayPaused) return;

        if (swiper.params.autoplay.enabled) {
          swiper.autoplay.resume();
        }

        this.$nextTick(() => {
          pauseAllVideos();
          playActiveVideo(true);
        });
      },
    }));
  }

  document.addEventListener("alpine:init", () => {
    Alpine.directive("video", (el, { modifiers, expression }, { evaluate }) => {
      const options = expression ? evaluate(expression) : {};
      options.exclusive = modifiers.includes("exclusive");

      root(Alpine, el, options);
    });

    /**
     * `$swiperVideo` magic helper.
     *
     * Returns a function that registers a Swiper instance for coordinated
     * video playback. Call it from an Alpine `x-init` or `init()` method
     * after the Swiper instance is available.
     *
     * @example
     *   <div x-data="{ init() { this.$swiperVideo(mySwiper); } }">
     */
    Alpine.magic("swiperVideo", (el) => {
      return (swiper) => handleSwiperVideoControl(Alpine, swiper, el);
    });
  });
})();
